import AddCostForm, { CostFormData } from '@/components/features/shared/costs/AddCostForm';
import { useCostTypeOptions } from '@/components/features/shared/costs/useCostTypes';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { useTranslations } from '@/hooks/useTranslations';
import { Project } from '@/types';
import { useForm } from '@inertiajs/react';
import { FormEventHandler, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

interface AddProjectCostModalProps {
    project: Project;
    trigger?: React.ReactNode;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

const createCostSchema = (costTypes: string[]) => {
    return z.object({
        description: z.string().min(1, 'Description is required'),
        amount: z
            .string()
            .min(1, 'Amount is required')
            .refine((value) => {
                const numeric = Number(value);
                return !Number.isNaN(numeric) && numeric >= 0;
            }, 'Enter a valid amount'),
        type: costTypes.length > 0 ? z.enum(costTypes as [string, ...string[]]) : z.string().min(1, 'Select a valid cost type'),
        date_incurred: z.date(),
    });
};

export default function AddProjectCostModal({ project, trigger, open: controlledOpen, onOpenChange }: AddProjectCostModalProps) {
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = controlledOpen !== undefined && onOpenChange !== undefined;
    const open = isControlled ? controlledOpen : internalOpen;
    const setOpen = (value: boolean) => {
        if (isControlled && onOpenChange) {
            onOpenChange(value);
        } else {
            setInternalOpen(value);
        }
    };
    const { t } = useTranslations();
    const [validationErrors, setValidationErrors] = useState<Partial<Record<keyof CostFormData, string>>>({});
    const costTypeOptions = useCostTypeOptions();
    const defaultType = costTypeOptions.length > 0 ? costTypeOptions[0].value : 'other';

    const { data, setData, post, processing, errors, reset } = useForm<CostFormData>({
        description: '',
        amount: '',
        type: defaultType,
        date_incurred: new Date(),
    });

    const clearValidationError = (field: keyof CostFormData) => {
        setValidationErrors((prev) => {
            if (!prev[field]) {
                return prev;
            }
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const handleOpenChange = (next: boolean) => {
        setOpen(next);
        if (!next) {
            reset();
            setValidationErrors({});
        }
    };

    const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
        event.preventDefault();
        const costTypes = costTypeOptions.map((opt) => opt.value);
        const schema = createCostSchema(costTypes);
        const result = schema.safeParse(data);

        if (!result.success) {
            const fieldErrors: Partial<Record<keyof CostFormData, string>> = {};
            result.error.issues.forEach((issue) => {
                const field = issue.path[0];
                if (field) {
                    fieldErrors[field as keyof CostFormData] = issue.message;
                }
            });
            setValidationErrors(fieldErrors);
            toast.error(t('toasts.components_addprojectcostmodal.resolve_errors', 'Please resolve the highlighted errors.'));
            return;
        }

        setValidationErrors({});

        post(`/projects/${project.id}/cost-lines`, {
            onSuccess: () => {
                handleOpenChange(false);
                toast.success(t('toasts.components_addprojectcostmodal.cost_added', 'Cost added successfully'));
            },
            onError: () => {
                toast.error(t('toasts.components_addprojectcostmodal.add_failed', 'Failed to add cost'));
            },
        });
    };

    return (
        <ResponsiveModal open={open} onOpenChange={handleOpenChange} variant="sheet">
            {!isControlled && trigger ? <ResponsiveModalTrigger asChild>{trigger}</ResponsiveModalTrigger> : null}
            <ResponsiveModalContent size="md">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>Add Cost</ResponsiveModalTitle>
                    <ResponsiveModalDescription>Add a new cost to this project. This will be subtracted from the budget.</ResponsiveModalDescription>
                </ResponsiveModalHeader>

                {/* The form supplies its own action row, so it is the whole body. */}
                <ResponsiveModalBody>
                    <AddCostForm
                        entityType="project"
                        entity={project}
                        data={data}
                        setData={(field, value) => {
                            if (value === null || value === undefined) {
                                if (field === 'date_incurred') {
                                    setData(field, new Date());
                                } else {
                                    setData(field, '');
                                }
                            } else {
                                setData(field, value as string | Date);
                            }
                        }}
                        errors={{ ...errors, ...validationErrors }}
                        clearValidationError={clearValidationError}
                        onSubmit={handleSubmit}
                        processing={processing}
                    />
                </ResponsiveModalBody>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
